import api from '../api';

export interface ValidationResult {
  success: boolean;
  fileName: string;
  fileSize: number;
  parsedData: {
    engagement: EngagementData | null;
    laborEconomicsCount: number;
    laborEconomics: LaborEconomicsData[];
    processMetricsCount: number;
    processMetrics: ProcessMetricsData[];
    opportunityMetricsCount: number;
    opportunityMetrics: OpportunityMetricsData[];
    nonFinancialBenefitsCount: number;
  };
  matchResults: {
    matched: number;
    unmatched: number;
    fuzzyMatches: FuzzyMatch[];
    opportunityMatches: OpportunityMatch[];
    unmatchedOpportunities: UnmatchedOpportunity[];
  };
  validationErrors: ValidationError[];
  warnings: Warning[];
  summary: ImportSummary;
}

export interface EngagementData {
  client_name: string;
  engagement_name: string;
  analyst_name: string;
  date_prepared: string;
  data_confidence_level: string;
  data_sources: string;
  notes: string;
}

export interface LaborEconomicsData {
  role_name: string;
  hourly_rate: number;
  fte_count: number;
  annual_hours: number;
  notes?: string;
}

export interface ProcessMetricsData {
  process_name: string;
  weekly_volume: number;
  volume_variability: number;
  cycle_time_hours: number;
  cycle_time_variability: number;
  fte_hours_per_week: number;
  fte_hours_variability: number;
  primary_roles: string;
  error_rate: number;
  error_rate_variability: number;
  avg_rework_time_hours: number;
  customer_impact: string;
  compliance_risk_level: string;
  data_source_notes: string;
}

export interface OpportunityMetricsData {
  opportunity_id_input: string;
  opportunity_name: string;
  linked_process: string;
  effort_reduction_pct: number;
  effort_range_low: number;
  effort_range_high: number;
  cycle_time_reduction_pct: number;
  cycle_range_low: number;
  cycle_range_high: number;
  error_reduction_pct: number;
  error_range_low: number;
  error_range_high: number;
  impl_cost_software: number;
  impl_cost_services: number;
  impl_cost_internal: number;
  cost_variability: number;
  ongoing_annual_cost: number;
  implementation_weeks: number;
  timeline_variability: number;
  confidence_level: string;
  assumptions_notes: string;
}

export interface FuzzyMatch {
  type: string;
  imported: string;
  matched: string;
  similarity: number;
  suggestions: string[];
}

export interface OpportunityMatch {
  imported: string;
  matched: string;
  similarity: string;
  isExact: boolean;
}

export interface UnmatchedOpportunity {
  name: string;
  suggestions: { name: string; similarity: number }[];
}

export interface ValidationError {
  sheet: string;
  row?: number;
  field: string;
  message: string;
}

export interface Warning {
  sheet: string;
  message: string;
}

export interface ImportSummary {
  engagement: number;
  laborEconomics: number;
  processMetrics: number;
  opportunityMetrics: number;
  nonFinancialBenefits: number;
}

export interface ImportResult {
  success: boolean;
  message: string;
  summary: {
    engagement: boolean;
    laborEconomics: number;
    processMetrics: number;
    opportunityMetrics: number;
    nonFinancialBenefits: number;
    opportunitiesUpdated: number;
  };
  matchResults: {
    matched: number;
    unmatched: number;
  };
  roiCalculations: ROICalculation[];
  warnings: Warning[];
}

export interface ROICalculation {
  opportunity_name: string;
  opportunity_id: number | null;
  annual_hours_saved: number;
  weighted_hourly_rate: number;
  annual_dollar_value: number;
  error_reduction_value: number;
  total_annual_benefit: number;
  annual_savings: number;
  total_implementation_cost: number;
  ongoing_annual_cost: number;
  npv_3year: number;
  roi_3year_pct: number;
  roi_percentage: number;
  payback_months: number | null;
  inputs_hash: string;
  calculation_version: string;
  calculated_at: string;
  data_source: string;
}

export interface GroundedStatus {
  total: number;
  grounded: number;
  estimated: number;
  percentageGrounded: string;
  opportunities: {
    id: number;
    title: string;
    isGrounded: boolean;
    groundedDate: string | null;
  }[];
}

export interface ImportHistoryEntry {
  id: number;
  project_id: number;
  imported_by: number;
  file_name: string;
  file_size: number;
  import_status: string;
  records_imported: object;
  validation_errors: ValidationError[];
  import_summary: object;
  created_at: string;
}

export const metricsImportAPI = {
  validateFile: async (projectId: number, file: File): Promise<ValidationResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/api/metrics-import/validate/${projectId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  },

  importFile: async (
    projectId: number,
    file: File,
    importUnmatched: boolean = false
  ): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('importUnmatched', String(importUnmatched));

    const response = await api.post(
      `/api/metrics-import/import/${projectId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  },

  getImportHistory: async (projectId: number): Promise<ImportHistoryEntry[]> => {
    const response = await api.get(`/api/metrics-import/history/${projectId}`);
    return response.data.history;
  },

  getGroundedStatus: async (projectId: number): Promise<GroundedStatus> => {
    const response = await api.get(`/api/metrics-import/grounded-status/${projectId}`);
    return response.data;
  },

  recalculateROI: async (projectId: number): Promise<ROICalculation[]> => {
    const response = await api.post(`/api/metrics-import/recalculate-roi/${projectId}`);
    return response.data.results;
  }
};

export default metricsImportAPI;
